import UIComponent from "sap/ui/core/UIComponent";
import LoginService from "ips/ac/util/LoginService";
import UserService from "ips/ac/data/user/service/UserService";
import JSONModel from "sap/ui/model/json/JSONModel";
import OrderLabelService from "ips/ac/data/order/label/service/OrderLabelService";
import ValueHelpService from "ips/ac/data/valuehelp/service/ValueHelpService";
import InternalOrderStatusService from "ips/ac/data/internal-order-status/service/InternalOrderStatusService";
import ODataMetaModel from "sap/ui/model/odata/v4/ODataMetaModel";
import FlexibleColumnLayout from "sap/f/FlexibleColumnLayout";
import { LayoutType } from "sap/f/library";
import Device from "sap/ui/Device";
import FlexibleColumnLayoutSemanticHelper from "sap/f/FlexibleColumnLayoutSemanticHelper";
import Router from "sap/f/routing/Router";
import Config from "ips/ac/config/Config";
import View from "sap/ui/core/mvc/View";
import UserClassObj from "ips/ac/util/classes/user/User";
import Control from "sap/ui/core/Control";
import Util from "ips/ac/util/Util";
import ODataContextBinding from "sap/ui/model/odata/v4/ODataContextBinding";
import ODataModel from "sap/ui/model/odata/v4/ODataModel";
import ThemeService from "ips/ac/base/service/ThemeService";
import SplashScreen from "ips/ac/util/SplashScreen";

/**
 * @namespace ips.ac
 */
export default class Component extends UIComponent {

  public _mFragments: { [key: string]: Control } = {};
  public refreshCalendar = {
    taskCalendar: false,
    teamCalendar: false,
  };
  private user: UserClassObj;
  private currentRoute: string;
  private isLoaded: Promise<void>;
  private config: Config;
  public loadedViews: Record<string, View> = {};

  public init(): void {
    super.init();

    //dont delete or remove the whole prototype.js
    sap.ui.require(["ips/ac/util/prototype"], (prototype) => {});

    this.config = new Config();
    this.user = UserClassObj.getInstance();

    this.bootstrap().catch();
  }

  private async bootstrap(): Promise<void> {

    this.user.syncAppModel();
    this.startSessionPingService();
    const resolve: Function = this.initLoadedPromise();
    await (this.getModel().getMetaModel() as ODataMetaModel).requestData();
    // await this.refreshUser();
    void ThemeService.getInstance().applyThemes();
    //triggers after loaded
    resolve();
    await this.loadJsonModels();
    SplashScreen.remove();
    this.getRouter().initialize();

  }

  public getConfig(): Config {
    return this.config;
  }

  public async refreshUser(): Promise<void> {
    await UserService.loadCurrentUser();
  }


  //@Todo Replace
  public getHelper(): FlexibleColumnLayoutSemanticHelper {
    let FCL: FlexibleColumnLayout = (this.getRootControl() as View).byId("idAppControl") as FlexibleColumnLayout;
    const settings: object = {
      defaultTwoColumnLayoutType: LayoutType.TwoColumnsBeginExpanded, // default
      defaultThreeColumnLayoutType: LayoutType.ThreeColumnsMidExpanded, // default
      initialColumnsCount: 1,
      maxColumnsCount: 3,
    };

    return FlexibleColumnLayoutSemanticHelper.getInstanceFor(
      FCL,
      settings,
    );
  }

  //@Todo Replace
  public getRouteLayout(route: string): LayoutType {
    const router: Router = this.getRouter() as Router;
    let routeTarget: any = router.getTarget(route);
    if (!routeTarget) {
      return routeTarget;
    }

    const targetConfig = (routeTarget as any)._oOptions as {
      viewPath?: string;
      viewName?: string;
      controlAggregation?: string;
    };
    const viewName: string = targetConfig.viewPath + "." + targetConfig.viewName;
    const views: Record<string, { layout?: LayoutType }> = this.getConfigOption("views");
    const cfg: any = views?.[viewName] || {};
    let layout = cfg.layout;
    if (!layout) {
      switch (targetConfig.controlAggregation) {
        case "midColumnPages":
          layout = LayoutType.TwoColumnsMidExpanded;
          break;
        default:
          layout = LayoutType.OneColumn;
          break;
      }
    }
    return layout;
  }

  //TODO: check if needed
  public setLayout(layout: string): void {
    this.getAppModel().setProperty("/layout", layout);
  }

  public getCurrentRoute(): string {
    return this.currentRoute;
  }

  public setCurrentRoute(route: string): string {
    this.currentRoute = route;
    return this.currentRoute;
  }

  //TODO: check if needed
  public getConfigOption(optionName: string) {
    const config: any = this.getManifestEntry("ips.ac");
    const keys: string[] = optionName.split(".");
    let node: any = config;

    for (let i = 0; i < keys.length; i++) {
      if (node[keys[i]] === undefined) {
        //return undefined;
        return {};
      }
      node = node[keys[i]];
    }

    return node;
  }

  public getContentDensityClass(): string {
    return Device.support.touch ? "sapUiSizeCozy" : "sapUiSizeCompact";
  }

  /**
   * @deprecated Use `ValueHelpService.refreshValueHelpModel` instead.
   */
  public async refreshSmartValueHelpModel(): Promise<void> {
    return ValueHelpService.refreshValueHelpModel();
  }

  /**
   * @deprecated Use `OrderLabelService.refreshOrderLabelModel` instead.
   */
  public async refreshOrderLabelsModel(): Promise<void> {
    await OrderLabelService.refreshOrderLabelModel();
    return;
  };

  public getAppModel(): JSONModel {
    return this.getModel("app") as JSONModel;
  };

  public getUser(): UserClassObj {
    return this.user;
  }

  private async loadJsonModels(): Promise<any> {
    const deviceModel = new JSONModel(Device);
    deviceModel.setDefaultBindingMode("OneWay");
    this.setModel(deviceModel, "device");
    this.initIpsConfig();

    return await Promise.all([
      OrderLabelService.refreshOrderLabelModel(),
      ValueHelpService.refreshValueHelpModel(),
      InternalOrderStatusService.refreshInternalOrderStatusModel(),
      UserService.refreshUsersModel(),
    ]);
  }

  private initIpsConfig(): void {
    const config = this.getConfig();
    const model: JSONModel = this.getModel("ips_config") as JSONModel;
    model.setData({
      TransferApi:
        config.getValue("BackendUrl") + config.getValue("TransferApi"),
      FileUploadApi:
        config.getValue("BackendUrl") + config.getValue("FileUploadApi"),
      DownloadApi:
        config.getValue("BackendUrl") + config.getValue("ODataApi"),
      UploadApi:
        config.getValue("BackendUrl") + config.getValue("UploadApi"),
      RestApi: config.getRestUrl(),
      BackendUrl: config.getValue("BackendUrl"),
    });
  };

  private startSessionPingService(): void {
    setInterval(() => LoginService.getInstance().checkLogin(), 1000 * 60 * 5);
  }

  private initLoadedPromise(): Function {
    let res: Function;
    this.isLoaded = new Promise((resolve) => {
      res = resolve;
    });
    return res;

  };

  public isUserLoaded(): Promise<void> {
    return this.isLoaded;
  };

  public async changeLanguage(language: string) {
    const userGuid = UserClassObj.getInstance().getGuid();
    const oDataModel = Util.getComponent().getModel() as ODataModel;
    const userContext = oDataModel.bindContext("/Users('" + userGuid + "')") as ODataContextBinding;
    userContext.getBoundContext().setProperty("Language_Value", language);
    oDataModel.submitBatch(oDataModel.getUpdateGroupId()).then(() => {
      UserClassObj.getInstance().setLanguage(language);
      window.location.reload();
    });
  }

  public setLoadedView(name: string, view: View): void {
    this.loadedViews[name] = view;
  }

  public getLoadedView(viewName: string): View {
    return this.loadedViews[viewName];
  }
}